type KvValue = unknown;

const DB_NAME = 'mynazcaApp';
const LEGACY_DB_NAME = 'fitToPathApp';
const DB_VERSION = 1;
const STORE = 'kv';

const LEGACY_LS_PREFIX = 'fitToPath.';

export const LS_KEYS = {
  persistEdit: 'mynazca.persistEdit',
  persistRoute: 'mynazca.persistRoute',
  persistSave: 'mynazca.persistSave',
  options: 'mynazca.options',
  optionsStrokeTrace: 'mynazca.options.strokeTrace',
  optionsDensity: 'mynazca.options.density',
  bgCompositeColor: 'mynazca.bgCompositeColor',
  autoBgRemoved: 'mynazca.autoBgRemoved',
  openEditSection: 'mynazca.openEditSection',
  mapCenter: 'mynazca.mapCenter',
  autoPersistImageLegacy: 'mynazca.autoPersistImage',
} as const;

export const IDB_KEYS = {
  workingBlob: 'workingBlob',
  imagePathResult: 'imagePathResult',
} as const;

let legacyStorageMigrated = false;

function migrateLegacyLocalStorageKey(newKey: string): void {
  const legacyKey = `${LEGACY_LS_PREFIX}${newKey.slice('mynazca.'.length)}`;
  try {
    if (localStorage.getItem(newKey) !== null) return;
    const legacy = localStorage.getItem(legacyKey);
    if (legacy === null) return;
    localStorage.setItem(newKey, legacy);
    localStorage.removeItem(legacyKey);
  } catch { /* noop */ }
}

function migrateLegacyLocalStorageOnce(): void {
  if (legacyStorageMigrated) return;
  legacyStorageMigrated = true;
  for (const key of Object.values(LS_KEYS)) {
    migrateLegacyLocalStorageKey(key);
  }
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB'));
  });
}

async function idbGetFromDb<T>(dbName: string, key: string): Promise<T | null> {
  const db = await openDb(dbName);
  const value = await new Promise<T | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    tx.onerror = () => reject(tx.error ?? new Error('Failed to read IndexedDB'));
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror = () => reject(req.error ?? new Error('Failed to read IndexedDB'));
  });
  db.close();
  return value;
}

export async function idbSet(key: string, value: KvValue): Promise<void> {
  const db = await openDb(DB_NAME);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Failed to write IndexedDB'));
    tx.objectStore(STORE).put(value as any, key);
  });
  db.close();
}

export async function idbGet<T>(key: string): Promise<T | null> {
  migrateLegacyLocalStorageOnce();
  const value = await idbGetFromDb<T>(DB_NAME, key);
  if (value !== null) return value;

  const legacy = await idbGetFromDb<T>(LEGACY_DB_NAME, key);
  if (legacy !== null) {
    await idbSet(key, legacy);
  }
  return legacy;
}

export async function idbDel(key: string): Promise<void> {
  const db = await openDb(DB_NAME);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Failed to delete IndexedDB'));
    tx.objectStore(STORE).delete(key);
  });
  db.close();
}

export async function idbClearAll(): Promise<void> {
  const db = await openDb(DB_NAME);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Failed to clear IndexedDB'));
    tx.objectStore(STORE).clear();
  });
  db.close();
}

export function readPersistFlag(key: string, defaultValue = true): boolean {
  migrateLegacyLocalStorageOnce();
  try {
    const v = localStorage.getItem(key);
    if (v === 'false') return false;
    if (v === 'true') return true;
    return defaultValue;
  } catch {
    return defaultValue;
  }
}

export function writePersistFlag(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? 'true' : 'false');
  } catch { /* noop */ }
}

export type BgFillColor = '#ffffff' | '#000000';

export function normalizeBgFillColor(value: string | null | undefined): BgFillColor {
  if (value?.toLowerCase() === '#000000') return '#000000';
  return '#ffffff';
}

export type EditSectionId = 'crop' | 'background' | 'tone';

export function loadOpenEditSectionInitial(): EditSectionId | null {
  migrateLegacyLocalStorageOnce();
  try {
    const v = localStorage.getItem(LS_KEYS.openEditSection);
    if (v === 'crop' || v === 'background' || v === 'tone') return v;
  } catch { /* noop */ }
  return null;
}

export function loadBgCompositeColorInitial(): BgFillColor {
  migrateLegacyLocalStorageOnce();
  try {
    return normalizeBgFillColor(localStorage.getItem(LS_KEYS.bgCompositeColor));
  } catch {
    return '#ffffff';
  }
}

export function loadPersistEditInitial(): boolean {
  migrateLegacyLocalStorageOnce();
  try {
    const legacy = localStorage.getItem(LS_KEYS.autoPersistImageLegacy);
    if (legacy !== null) {
      const on = legacy !== 'false';
      writePersistFlag(LS_KEYS.persistEdit, on);
      localStorage.removeItem(LS_KEYS.autoPersistImageLegacy);
      return on;
    }
  } catch { /* noop */ }
  return readPersistFlag(LS_KEYS.persistEdit, true);
}

export function loadMapCenterFromStorage(): { lat: number; lng: number } | null {
  migrateLegacyLocalStorageOnce();
  try {
    const raw = localStorage.getItem(LS_KEYS.mapCenter);
    if (!raw) return null;
    const p = JSON.parse(raw) as { lat?: number; lng?: number };
    if (typeof p.lat === 'number' && typeof p.lng === 'number') return { lat: p.lat, lng: p.lng };
  } catch { /* noop */ }
  return null;
}

export function saveRouteOptionsToStorage(options: { routeMode?: string } & Record<string, unknown>): void {
  try {
    localStorage.setItem(LS_KEYS.options, JSON.stringify(options));
    if (options.routeMode === 'strokeTrace' || options.routeMode === 'density') {
      localStorage.setItem(
        options.routeMode === 'strokeTrace' ? LS_KEYS.optionsStrokeTrace : LS_KEYS.optionsDensity,
        JSON.stringify(options),
      );
    }
  } catch { /* noop */ }
}

export async function clearEditStorage(): Promise<void> {
  await idbDel(IDB_KEYS.workingBlob);
  try {
    localStorage.removeItem(LS_KEYS.bgCompositeColor);
    localStorage.removeItem(LS_KEYS.autoBgRemoved);
    localStorage.removeItem(LS_KEYS.openEditSection);
  } catch { /* noop */ }
}

export function clearRouteStorage(): void {
  try {
    localStorage.removeItem(LS_KEYS.options);
    localStorage.removeItem(LS_KEYS.optionsStrokeTrace);
    localStorage.removeItem(LS_KEYS.optionsDensity);
  } catch { /* noop */ }
}

export async function clearRoutePersistedData(): Promise<void> {
  clearRouteStorage();
  await idbDel(IDB_KEYS.imagePathResult);
}

export function clearSaveStorage(): void {
  try {
    localStorage.removeItem(LS_KEYS.mapCenter);
  } catch { /* noop */ }
}

export async function clearAllPersistedData(): Promise<void> {
  await clearEditStorage();
  clearRouteStorage();
  clearSaveStorage();
}
